import React from 'react';
import { ZohoInitialImportSample, ZohoInitialImportSamples, ZohoInitialImportSummary } from '../../services/zohoCRM';

interface ZohoInitialImportModalProps {
  show: boolean;
  isLoading: boolean;
  summary: ZohoInitialImportSummary | null;
  samples: ZohoInitialImportSamples | null;
  onClose: () => void;
  onRefreshPreview: () => void;
}

const sampleSections: Array<{
  key: keyof ZohoInitialImportSamples;
  label: string;
  emptyLabel: string;
}> = [
  { key: 'exactEmail', label: 'Exact Email', emptyLabel: 'No exact email matches found.' },
  { key: 'companyMatch', label: 'Company Match', emptyLabel: 'No normalized company matches found.' },
  { key: 'newCustomers', label: 'New Customers', emptyLabel: 'No new customers found.' },
  { key: 'review', label: 'Review', emptyLabel: 'No review items found.' },
  { key: 'duplicate', label: 'Duplicate', emptyLabel: 'No duplicate items found.' },
];

function renderSampleRow(sample: ZohoInitialImportSample, index: number) {
  return (
    <tr key={`${sample.zohoName}-${sample.zohoEmail || 'no-email'}-${index}`} className="border-t border-slate-100 align-top">
      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{sample.zohoName}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{sample.zohoEmail || 'N/A'}</td>
      <td className="px-4 py-3 text-sm text-slate-700">{sample.matchedCustomerName || 'N/A'}</td>
      <td className="px-4 py-3 text-sm font-mono text-xs text-slate-500">{sample.matchedCustomerId || 'N/A'}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{sample.matchReason}</td>
    </tr>
  );
}

const ZohoInitialImportModal: React.FC<ZohoInitialImportModalProps> = ({
  show,
  isLoading,
  summary,
  samples,
  onClose,
  onRefreshPreview,
}) => {
  if (!show) return null;

  const [showSamples, setShowSamples] = React.useState(false);

  React.useEffect(() => {
    if (show) setShowSamples(false);
  }, [show, summary, samples]);

  const safeLinked =
    (summary?.alreadyLinked || 0) +
    (summary?.exactEmailMatch || 0) +
    (summary?.exactPhoneMatch || 0) +
    (summary?.normalizedCompanyMatch || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[2.5rem] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-10 py-8">
          <div>
            <h3 className="text-2xl font-black text-slate-900">Initial Zoho Import Preview Audit</h3>
            <p className="mt-1 text-sm text-slate-500">
              Preview only. No import or customer changes are performed from this screen.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="max-h-[calc(92vh-8.5rem)] overflow-y-auto px-10 py-8">
          <div className="space-y-6">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
              Audit only. This preview shows sample Zoho records by match category and does not import anything.
            </div>

            {summary ? (
              <>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {[
                    { label: 'Already Linked', value: summary.alreadyLinked, tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                    { label: 'Exact Email', value: summary.exactEmailMatch, tone: 'bg-blue-50 text-blue-700 border-blue-200' },
                    { label: 'Exact Phone', value: summary.exactPhoneMatch, tone: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
                    { label: 'Company Match', value: summary.normalizedCompanyMatch, tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
                    { label: 'New Customers', value: summary.newCustomers, tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                    { label: 'Review', value: summary.reviewCount, tone: 'bg-rose-50 text-rose-700 border-rose-200' },
                    { label: 'Duplicates', value: summary.duplicateCount, tone: 'bg-amber-50 text-amber-700 border-amber-200' },
                    { label: 'Fuzzy', value: summary.fuzzyReviewCount, tone: 'bg-orange-50 text-orange-700 border-orange-200' },
                  ].map((item) => (
                    <div key={item.label} className={`rounded-2xl border px-4 py-3 ${item.tone}`}>
                      <p className="text-[10px] font-black uppercase tracking-widest">{item.label}</p>
                      <p className="mt-1 text-xl font-black">{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                  <p>Safe linked clients identified: <span className="font-black text-slate-900">{safeLinked}</span></p>
                  <p>Potential new clients identified: <span className="font-black text-slate-900">{summary.newCustomers}</span></p>
                  <p>Manual review left untouched: <span className="font-black text-slate-900">{summary.reviewCount + summary.duplicateCount}</span></p>
                </div>

                {summary.newCustomers === 0 ? (
                  <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-900">
                    No new safe customers to import. {safeLinked} linked customers can be refreshed from Zoho.
                  </div>
                ) : null}

                {showSamples ? (
                  <div className="space-y-6">
                    {sampleSections.map((section) => {
                      const items = samples?.[section.key] || [];
                      return (
                        <section key={section.key} className="rounded-3xl border border-slate-200 bg-white">
                          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                            <div>
                              <h4 className="text-lg font-black text-slate-900">{section.label}</h4>
                              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                                Showing up to 20 sample records
                              </p>
                            </div>
                            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                              {items.length} sample{items.length === 1 ? '' : 's'}
                            </div>
                          </div>

                          {items.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full">
                                <thead className="bg-slate-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-slate-500">Zoho Name</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-slate-500">Zoho Email</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-slate-500">Matched Customer Name</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-slate-500">Matched Customer ID</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest text-slate-500">Match Reason</th>
                                  </tr>
                                </thead>
                                <tbody>{items.map(renderSampleRow)}</tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="px-6 py-8 text-sm text-slate-500">{section.emptyLabel}</div>
                          )}
                        </section>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-6 text-sm text-slate-500">
                    Audit samples are available for Exact Email, Company Match, New Customers, Review, and Duplicate. Use “View Audit Samples” to inspect them.
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">
                Loading Zoho import preview...
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-100 px-10 py-6">
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`rounded-xl px-6 py-3 text-sm font-black uppercase ${
              isLoading ? 'bg-slate-200 text-slate-400' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Close
          </button>
          <button
            onClick={onRefreshPreview}
            disabled={isLoading}
            className={`rounded-xl px-6 py-3 text-sm font-black uppercase ${
              isLoading ? 'bg-slate-200 text-slate-400' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {isLoading ? 'Refreshing...' : 'Refresh Preview'}
          </button>
          <button
            onClick={() => setShowSamples(true)}
            disabled={isLoading || !summary}
            className={`rounded-xl px-6 py-3 text-sm font-black uppercase ${
              isLoading || !summary ? 'bg-slate-200 text-slate-400' : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            View Audit Samples
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZohoInitialImportModal;
